import React from 'react';

import { DutyHourTypeDynFlag } from 'generated';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@mui/material';

import { ControlledSelect, ControlledTextField } from 'components/shared';
import { ControlledFlagCheckbox } from 'components/shared/ControlledFields/ControlledFlagCheckbox';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import { InfoTooltip } from 'components/shared/InfoTooltip';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { WarningTooltip } from 'components/shared/WarningTooltip';

import { useGetDayCategoryOptions } from './hooks/useGetDayCategoryOptions';
import { useGetHoursCategorySvcOptions } from './hooks/useGetHoursCategorySvcOptions';
import { useCheckFlag } from '../../../../../../../../hooks/useCheckFlag';
import { useSelector } from 'hooks';
import { ControlledFlagMultiCheckboxSingleSelect } from 'components/shared/ControlledFields/ControlledFlagMultiCheckboxSingleSelect';

export const GeneralSettingsSection: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { hasTravelMovementsFeature, hasExternalHoursImport } = useSelector(
    ({ grants }) => grants
  );

  const getDayCategoryOptions = useGetDayCategoryOptions();
  const getHoursCategorySvcOptions = useGetHoursCategorySvcOptions();
  const isHoursPercSeparate = useCheckFlag<DutyHourTypeDynFlag>(
    'flags',
    'HOURS_PERC_SEPARATE'
  );
  const isHoursPercSeparateBuh = useCheckFlag<DutyHourTypeDynFlag>(
    'flags',
    'HOURS_PERC_SEPARATE_BUH'
  );
  const isHoursPercSeparateBonus = useCheckFlag<DutyHourTypeDynFlag>(
    'flags',
    'HOURS_PERC_SEPARATE_BONUS'
  );

  return (
    <MultiStepForm.Section
      title={t('dutyHourTypes.section.generalSettings')}
      validateFields={[]}
    >
      <ControlledTextField
        name="activityCode"
        control={control}
        label={t('dutyHourTypes.fieldName.activityExportCode')}
        variant="outlined"
      />
      <ControlledTextField
        name="activityCodeAlt"
        control={control}
        label={t('dutyHourTypes.fieldName.alternativeImportCode')}
        variant="outlined"
      />
      <ControlledNumericField
        name="salPerc"
        label={t('dutyHourTypes.fieldName.salaryPercentage')}
        variant="outlined"
      />
      {getHoursCategorySvcOptions.isLoading ? (
        <Skeleton />
      ) : (
        <ControlledSelect
          name="hoursCategorySvc"
          control={control}
          label={t('dutyHourTypes.fieldName.hoursCategory')}
          options={getHoursCategorySvcOptions.data ?? []}
          variant="outlined"
        />
      )}
      {getDayCategoryOptions.isLoading ? (
        <Skeleton />
      ) : (
        <ControlledSelect
          name="dayCategory.code"
          control={control}
          label={t('dutyHourTypes.fieldName.dayCategory')}
          options={getDayCategoryOptions.data ?? []}
          variant="outlined"
        />
      )}
      <ControlledNumericField
        name="salSupplPerDutyNominal"
        step={1}
        label={t('dutyHourTypes.fieldName.nomSalarySupplPerDuty')}
        variant="outlined"
      />
      <ControlledFlagCheckbox<DutyHourTypeDynFlag>
        name="flags"
        flag="TRAVEL_EXPENSES"
        label={t('dutyHourTypes.fieldName.travelExpenses')}
      />
      {hasTravelMovementsFeature && (
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="TRAVEL_MOVEMENTS"
          label={t('dutyHourTypes.fieldName.travelMovements')}
        />
      )}
      <ControlledFlagCheckbox<DutyHourTypeDynFlag>
        name="flags"
        flag="WORK_FROM_HOME"
        label={t('dutyHourTypes.fieldName.workFromHome')}
      />
      {hasExternalHoursImport && (
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="EXTERNAL_DUTY_MARK"
          label={t('dutyHourTypes.fieldName.externalHoursImport')}
        />
      )}
      <ControlledFlagCheckbox<DutyHourTypeDynFlag>
        name="flags"
        flag="BUH_BLOCKED_FLAG"
        label={t('dutyHourTypes.fieldName.noBUH')}
      />
      <ControlledFlagCheckbox<DutyHourTypeDynFlag>
        name="flags"
        flag="PLB_BLOCKED_FLAG"
        label={t('dutyHourTypes.fieldName.noPLBAccrual')}
      />
      <ControlledFlagCheckbox<DutyHourTypeDynFlag>
        name="flags"
        flag="COUNT_FOR_DAY_LIMIT"
        label={t('dutyHourTypes.fieldName.countForDayLimit')}
      />
      <WarningTooltip title={t('dutyHourTypes.tooltips.askForPermission')}>
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="BONUS_CRISIS"
          label={t('dutyHourTypes.fieldName.bonusCrisis')}
        />
      </WarningTooltip>
      <WarningTooltip title={t('dutyHourTypes.tooltips.askForPermission')}>
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="BONUS_CRISIS_100"
          label={t('dutyHourTypes.fieldName.bonusCrisis100')}
        />
      </WarningTooltip>
      <WarningTooltip title={t('dutyHourTypes.tooltips.askForPermission')}>
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="ROSTER_FREE"
          label={t('dutyHourTypes.fieldName.bonusFree')}
        />
      </WarningTooltip>
      <ControlledFlagCheckbox<DutyHourTypeDynFlag>
        name="flags"
        flag="EXTRA_COMPENSATION"
        label={t('dutyHourTypes.fieldName.extraCompensation')}
      />
      <ControlledFlagMultiCheckboxSingleSelect
        name="flags"
        label={t('dutyHourTypes.fieldName.additionalCompensation')}
        flags={[
          {
            flag: 'ADDITIONAL_COMPENSATION_STANDARD',
            label: t('dutyHourTypes.fieldName.additionalCompensationStandard')
          },
          {
            flag: 'ADDITIONAL_COMPENSATION_SLEEP',
            label: t('dutyHourTypes.fieldName.additionalCompensationSleep')
          },
          {
            flag: 'ADDITIONAL_COMPENSATION_DEVIANT',
            label: t('dutyHourTypes.fieldName.additionalCompensationDeviant')
          }
        ]}
      />
      <WarningTooltip title={t('dutyHourTypes.tooltips.askForPermission')}>
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="HOURS_PERC_SEPARATE"
          label={t('dutyHourTypes.fieldName.hoursPercentageSeparate')}
        />
      </WarningTooltip>
      {isHoursPercSeparate && (
        <>
          <InfoTooltip
            title={t('dutyHourTypes.tooltips.hoursPercSeparatePerc')}
          >
            <ControlledNumericField
              name="hoursPercSeparatePerc"
              label={t('dutyHourTypes.fieldName.hoursPercSeparatePerc')}
              variant="outlined"
            />
          </InfoTooltip>
          <InfoTooltip
            title={t('dutyHourTypes.tooltips.hoursPercSeparateJusPerc')}
          >
            <ControlledNumericField
              name="hoursPercSeparateJusPerc"
              label={t('dutyHourTypes.fieldName.hoursPercSeparateJusPerc')}
              variant="outlined"
            />
          </InfoTooltip>
          <InfoTooltip title={t('dutyHourTypes.tooltips.hoursPercComp')}>
            <ControlledNumericField
              name="hoursPercComp"
              label={t('dutyHourTypes.fieldName.hoursPercComp')}
              variant="outlined"
            />
          </InfoTooltip>
        </>
      )}
      <WarningTooltip title={t('dutyHourTypes.tooltips.askForPermission')}>
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="HOURS_PERC_SEPARATE_BUH"
          label={t('dutyHourTypes.fieldName.hoursPercentageORT')}
        />
      </WarningTooltip>
      {isHoursPercSeparateBuh && (
        <ControlledNumericField
          name="hoursPercSeparateBuhPerc"
          label={t('dutyHourTypes.fieldName.hoursPercSeparateBuhPerc')}
          variant="outlined"
        />
      )}
      <WarningTooltip title={t('dutyHourTypes.tooltips.askForPermission')}>
        <ControlledFlagCheckbox<DutyHourTypeDynFlag>
          name="flags"
          flag="HOURS_PERC_SEPARATE_BONUS"
          label={t('dutyHourTypes.fieldName.hoursPercentageCompensation')}
        />
      </WarningTooltip>
      {isHoursPercSeparateBonus && (
        <ControlledNumericField
          name="hoursPercSeparateBonusPerc"
          label={t('dutyHourTypes.fieldName.hoursPercSeparateBonusPerc')}
          variant="outlined"
        />
      )}
      <ControlledFlagCheckbox<DutyHourTypeDynFlag>
        name="flags"
        flag="OVERSTAFFED"
        label={t('dutyHourTypes.fieldName.overStaffed')}
      />
    </MultiStepForm.Section>
  );
};
